import { CONSTANTS, TILE_COUNT } from "./constants";
import { areAdjacent, wordFromPath } from "./board";
import { heatLevel, nextRung, perfectOrderTotal, scorePath } from "./scoring";
import type { Dictionary } from "./dictionary";
import type { Path, PuzzleDef, ScoreBreakdown, ShotResult, TileIndex, TraceRejection } from "./types";

/**
 * The Longshot game, §1. Pure state — no I/O, no rendering, no timers.
 *
 * The one invariant worth stating out loud: **nothing on this object exposes
 * dictionary validity before a shot is fired**. `preview()` returns the points a
 * trace would earn if it hit, and deliberately says nothing about whether it
 * will. That information asymmetry is the game (§1.2); a convenience getter
 * that leaked it would quietly remove the reason to play.
 */
export class LongshotGame {
  readonly puzzle: PuzzleDef;
  private readonly dict: Dictionary;

  /** Prior VALID words crossing each tile. Misses never touch this (§1.4). */
  private readonly uses: number[];
  private trace: TileIndex[] = [];
  private firedWords = new Set<string>();
  private shots: ShotResult[] = [];
  private _bullets: number;
  private _rung = 1;
  private _score = 0;

  constructor(puzzle: PuzzleDef, dict: Dictionary) {
    this.puzzle = puzzle;
    this.dict = dict;
    this.uses = new Array(TILE_COUNT).fill(0);
    this._bullets = CONSTANTS.BULLETS;
  }

  // ---------------------------------------------------------------- state

  get bullets(): number { return this._bullets; }
  get rung(): number { return this._rung; }
  get score(): number { return this._score; }
  get isOver(): boolean { return this._bullets <= 0; }
  // Both return COPIES. Handing out the live arrays leaks mutable internals,
  // and — because the engine mutates them in place — a React consumer memoising
  // on the reference would never see the trace change. Copying 0-10 ints per
  // render is not a cost worth thinking about.
  get shotLog(): readonly ShotResult[] { return [...this.shots]; }
  get currentPath(): Path { return [...this.trace]; }
  get currentWord(): string { return wordFromPath(this.puzzle.board, this.trace); }

  /** Heat level 0..HEAT_CAP for every tile — what the board tints on. */
  heatLevels(): number[] { return this.uses.map(heatLevel); }

  // ---------------------------------------------------------------- trace

  /**
   * Extend the trace. Returns false (and changes nothing) if the tile is not
   * adjacent to the tail or is already in this word — reuse is per-word (§1.2).
   *
   * Tapping the current tail backs it off, which is how a dragged trace unwinds.
   */
  traceTile(tile: TileIndex): boolean {
    if (!Number.isInteger(tile) || tile < 0 || tile >= TILE_COUNT) return false;
    if (this.isOver) return false;

    if (this.trace.length > 0 && this.trace[this.trace.length - 1] === tile) {
      this.trace.pop();
      return true;
    }
    if (this.trace.includes(tile)) return false;
    if (this.trace.length > 0 && !areAdjacent(this.trace[this.trace.length - 1], tile)) return false;

    this.trace.push(tile);
    return true;
  }

  untrace(): void { this.trace.pop(); }
  clear(): void { this.trace = []; }

  /**
   * Points the current trace would score if it turned out to be a word.
   * Says nothing about whether it is one.
   */
  preview(): ScoreBreakdown {
    return scorePath(this.puzzle.board, this.trace, this.uses, this.puzzle.gold, this._rung);
  }

  /** Why the trace cannot be fired, or null if it can. Never dictionary-based. */
  fireRejection(): TraceRejection | null {
    if (this._bullets <= 0) return "no-bullets";
    const word = this.currentWord;
    if (word.length < CONSTANTS.MIN_LEN) return "too-short";
    if (word.length > CONSTANTS.MAX_LEN) return "too-long";
    if (this.firedWords.has(word)) return "already-fired";
    return null;
  }

  get canFire(): boolean { return this.fireRejection() === null; }

  // ----------------------------------------------------------------- fire

  /**
   * Spend a bullet on the current trace. Costs one either way (§1.2).
   *
   * Ordering is deliberate: score against `uses` as it stands, THEN bump the
   * crossed tiles. A word never earns heat from itself.
   */
  fire(): ShotResult {
    const rejection = this.fireRejection();
    if (rejection) throw new Error(`cannot fire: ${rejection}`);

    const word = this.currentWord;
    const path = [...this.trace];
    const valid = this.dict.has(word);
    const breakdown = scorePath(this.puzzle.board, path, this.uses, this.puzzle.gold, this._rung);

    const rungBefore = this._rung;
    const rungAfter = nextRung(rungBefore, valid);

    if (valid) {
      this._score += breakdown.points;
      for (const t of path) this.uses[t] += 1;
    }

    this._rung = rungAfter;
    this._bullets -= 1;
    this.firedWords.add(word);
    this.trace = [];

    const result: ShotResult = {
      word,
      path,
      valid,
      base: valid ? breakdown.base : 0,
      rung: rungBefore,
      points: valid ? breakdown.points : 0,
      heatPoints: valid ? breakdown.heatPoints : 0,
      gold: breakdown.gold,
      rungAfter,
    };
    this.shots.push(result);
    return result;
  }

  // --------------------------------------------------------------- ticket

  /**
   * End-of-game ticket numbers (§1.7). `perfectTotal` replays the realised
   * bases in ascending order on a clean ladder — same shots, better sequencing —
   * so `delta` is what sloppy ordering cost, not what a perfect game was worth.
   */
  ticket() {
    const validShots = this.shots.filter((s) => s.valid);
    const perfectTotal = perfectOrderTotal(validShots.map((s) => s.base));
    return {
      score: this._score,
      shots: this.shots,
      hits: validShots.length,
      misses: this.shots.length - validShots.length,
      perfectTotal,
      delta: Math.max(0, perfectTotal - this._score),
      bestColdWord: this.puzzle.analytics.bestColdWord,
      bestColdBase: this.puzzle.analytics.bestColdBase,
      bestColdPotential: this.puzzle.analytics.bestColdBase * CONSTANTS.MULT_MAX,
    };
  }
}
